import { useMemo, useState } from "react";
import { Download, IndianRupee } from "lucide-react";
import { ApiError } from "@/lib/api";
import { Badge, Button, Field, Input, Select } from "@/components/ui/primitives";
import { RequirePermission } from "@/features/auth/RequirePermission";
import { useExportReport, useReport } from "./hooks";
import type { ExportFormat, ReportFilters } from "./api";

const CALL_TYPES = [
  { value: "", label: "All call types" },
  { value: "amc", label: "AMC Call" },
  { value: "one_time", label: "One Time" },
  { value: "paid", label: "Paid Call" },
  { value: "free", label: "Free" },
] as const;

const TYPE_COL = 4; // "Call type" column index
const AMOUNT_COL = 5; // "Amount (₹)" column index

function money(n: number): string {
  return n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function CallRevenuePage() {
  const [callType, setCallType] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [exportError, setExportError] = useState<string | null>(null);

  const filters = useMemo<ReportFilters>(() => {
    const f: ReportFilters = {};
    if (callType) f.call_type = callType;
    if (start) f.start = start;
    if (end) f.end = end;
    return f;
  }, [callType, start, end]);

  const { data, isLoading, isError, error } = useReport("call-revenue", filters);
  const exportReport = useExportReport();

  // Rows minus the trailing TOTAL row the report appends.
  const dataRows = (data?.rows ?? []).filter((r) => r[0] !== "TOTAL");

  const summary = useMemo(() => {
    const byType: Record<string, { count: number; amount: number }> = {};
    let total = 0;
    for (const r of dataRows) {
      const label = String(r[TYPE_COL] ?? "");
      const amt = Number(r[AMOUNT_COL] ?? 0) || 0;
      total += amt;
      byType[label] = byType[label] ?? { count: 0, amount: 0 };
      byType[label].count += 1;
      byType[label].amount += amt;
    }
    return { total, byType, count: dataRows.length };
  }, [dataRows]);

  async function runExport(format: ExportFormat) {
    setExportError(null);
    try {
      await exportReport.mutateAsync({ name: "call-revenue", format, filters });
    } catch (err) {
      setExportError(err instanceof ApiError ? err.message : "Export failed.");
    }
  }

  const cards = [
    { label: "Total revenue", value: `₹${money(summary.total)}`, accent: "text-sla-ok" },
    { label: "Paid calls", sub: byLine(summary.byType["Paid Call"]) },
    { label: "One Time", sub: byLine(summary.byType["One Time"]) },
    { label: "AMC / Free", sub: `${(summary.byType["AMC Call"]?.count ?? 0)} AMC · ${(summary.byType["Free"]?.count ?? 0)} Free` },
  ];

  return (
    <div className="space-y-5">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
          <IndianRupee className="h-6 w-6 text-sla-ok" aria-hidden="true" />
          Call Revenue
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Every classified call with its amount and live status. Filter by call type
          and date, and export to CSV, Excel or PDF.
        </p>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {cards.map((c) => (
          <div key={c.label} className="rounded-lg border border-border bg-card p-4 text-card-foreground">
            <div className="text-sm text-muted-foreground">{c.label}</div>
            <div className={`mt-1 text-xl font-semibold tabular-nums ${c.accent ?? ""}`}>
              {c.value ?? c.sub}
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3 rounded-lg border border-border bg-muted/20 p-4">
        <Field label="Call type" htmlFor="cr_type">
          <Select id="cr_type" value={callType} onChange={(e) => setCallType(e.target.value)}>
            {CALL_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </Select>
        </Field>
        <Field label="From" htmlFor="cr_start">
          <Input id="cr_start" type="date" value={start} onChange={(e) => setStart(e.target.value)} />
        </Field>
        <Field label="To" htmlFor="cr_end">
          <Input id="cr_end" type="date" value={end} onChange={(e) => setEnd(e.target.value)} />
        </Field>
        <div className="ml-auto flex items-end gap-2">
          <RequirePermission permission="report:export">
            {(["csv", "xlsx", "pdf"] as ExportFormat[]).map((f) => (
              <Button
                key={f}
                variant="outline"
                disabled={exportReport.isPending || dataRows.length === 0}
                onClick={() => void runExport(f)}
              >
                <Download className="h-4 w-4" aria-hidden="true" />
                {f.toUpperCase()}
              </Button>
            ))}
          </RequirePermission>
        </div>
      </div>

      {exportError && (
        <div role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {exportError}
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full min-w-[720px] text-sm">
          <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
            <tr>
              {(data?.columns ?? []).map((c) => (
                <th key={c} className="px-4 py-2.5 font-medium">{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td className="px-4 py-8 text-center text-muted-foreground">Loading…</td></tr>
            )}
            {isError && (
              <tr>
                <td className="px-4 py-8 text-center text-destructive">
                  {error instanceof ApiError ? error.message : "Could not load call revenue."}
                </td>
              </tr>
            )}
            {data && dataRows.length === 0 && !isLoading && (
              <tr>
                <td colSpan={data.columns.length} className="px-4 py-8 text-center text-muted-foreground">
                  No classified calls for this selection.
                </td>
              </tr>
            )}
            {data?.rows.map((row, i) => {
              const isTotal = row[0] === "TOTAL";
              return (
                <tr
                  key={i}
                  className={"border-t border-border " + (isTotal ? "bg-muted/40 font-semibold" : "")}
                >
                  {row.map((cell, j) => (
                    <td key={j} className={"px-4 py-2.5 " + (j === AMOUNT_COL ? "tabular-nums" : "")}>
                      {j === TYPE_COL && !isTotal && cell ? (
                        <Badge tone={typeTone(String(cell))}>{cell}</Badge>
                      ) : (
                        cell ?? "—"
                      )}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function byLine(v: { count: number; amount: number } | undefined): string {
  if (!v || v.count === 0) return "—";
  return `${v.count} · ₹${money(v.amount)}`;
}

function typeTone(label: string): "success" | "warning" | "muted" {
  if (label === "Paid Call") return "success";
  if (label === "One Time") return "warning";
  return "muted";
}
